/**
 * `<VinculoPicker>` — a escolha do vínculo da solicitação (FX-8). Os tipos e a validação vivem em
 * `vinculo-solicitacao.ts` (arquivo sem componente, por causa do react-refresh).
 *
 * Usado por DOIS donos desde a Fase 2 (16/09/2026): o formulário "Nova solicitação" e o diálogo
 * "Classificar" do Quadro — o trabalho que era da aba Fila, que morreu na fusão.
 */
import { useMemo } from 'react';

import {
  BuscaCombobox,
  ClienteV2Combobox,
  Label,
  Select,
  SelectOption,
  type BuscaItem,
} from '@/components/ui';
import { useProjetos } from '@/lib/projetos-store';
import { useDeals } from '@/lib/comercial-store';
import { useSetores, useTiposItem } from '@/lib/compras-store';
import type { TipoVinculo } from '@/lib/solicitacoes-compra-store';

import { VINCULO_VAZIO, type VinculoState } from './vinculo-solicitacao';

export function VinculoPicker({ value, onChange }: { value: VinculoState; onChange: (v: VinculoState) => void }) {
  const projetos = useProjetos({});
  const deals = useDeals('comercial');
  const setores = useSetores();
  const tipos = useTiposItem();
  const set = (patch: Partial<VinculoState>) => onChange({ ...value, ...patch });

  // FX-11: listas em memória → busca por nome (combobox) pros seletores Projeto/Deal.
  const projetoItems: BuscaItem[] = useMemo(
    () =>
      (projetos.data?.items ?? []).map((p) => ({
        id: p.id,
        label: p.nome,
        sublabel: p.numeroHumano ? `#${p.numeroHumano}` : undefined,
      })),
    [projetos.data],
  );
  const dealItems: BuscaItem[] = useMemo(
    () => deals.map((d) => ({ id: d.id, label: d.titulo || '(sem título)', sublabel: d.humanLabel })),
    [deals],
  );

  return (
    <div className="space-y-2">
      <Label>Vínculo da solicitação</Label>
      <Select
        size="sm"
        value={value.tipoVinculo}
        onChange={(e) => onChange({ ...VINCULO_VAZIO, tipoVinculo: e.target.value as TipoVinculo | '' })}
      >
        <SelectOption value="">— sem vínculo (o Compras define) —</SelectOption>
        <SelectOption value="projeto">Projeto (obra)</SelectOption>
        <SelectOption value="deal">Negócio (venda comercial)</SelectOption>
        <SelectOption value="cliente">Cliente</SelectOption>
        <SelectOption value="interno">Uso interno</SelectOption>
      </Select>

      {value.tipoVinculo === 'projeto' && (
        <BuscaCombobox
          value={value.projetoId}
          onChange={(projetoId) => set({ projetoId })}
          items={projetoItems}
          placeholder="Buscar projeto por nome…"
          emptyText="Nenhum projeto encontrado"
        />
      )}

      {value.tipoVinculo === 'deal' && (
        <BuscaCombobox
          value={value.dealId}
          onChange={(dealId) => set({ dealId })}
          items={dealItems}
          placeholder="Buscar negócio por título…"
          emptyText="Nenhum negócio encontrado"
        />
      )}

      {value.tipoVinculo === 'cliente' && (
        <ClienteV2Combobox value={value.clienteId} onChange={(clienteId) => set({ clienteId })} />
      )}

      {value.tipoVinculo === 'interno' && (
        <div className="flex flex-wrap gap-2">
          <Select size="sm" className="min-w-[160px]" value={value.setorId} onChange={(e) => set({ setorId: e.target.value })}>
            <SelectOption value="">— setor —</SelectOption>
            {(setores.data?.items ?? []).map((s) => (
              <SelectOption key={s.id} value={s.id}>{s.nome}</SelectOption>
            ))}
          </Select>
          <Select size="sm" className="min-w-[160px]" value={value.categoriaId} onChange={(e) => set({ categoriaId: e.target.value })}>
            <SelectOption value="">— categoria —</SelectOption>
            {(tipos.data?.items ?? []).map((t) => (
              <SelectOption key={t.id} value={t.id}>{t.nome}</SelectOption>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
}
